import StaffOriginalSection from '../../features/staffNotes/StaffOriginalSection';
import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  ButtonBase,
  Collapse,
  Dialog,
  IconButton,
  Alert,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowForwardRounded as ArrowForwardIcon,
  Close as CloseIcon,
  DescriptionOutlined as DescriptionIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import colors from '../../assets/theme/colors';
import {
  CareKnowledgeSuggestionReview,
} from '../../features/careSourceProcessing';
import CareSourceReviewableCandidateActions from '../../features/careSourceProcessing/CareSourceReviewableCandidateActions';
import {
  buildGenericCareKnowledgeSource,
  buildReviewableCandidatesFromCareSource,
  splitReviewableCareKnowledgeSuggestions,
} from '../../features/careSourceProcessing/careSourceReviewableCandidates';
import {
  fetchCareSourceById,
} from '../../features/careSourceProcessing/careSourceActivityService';
import { hasMedicationSourceSuggestions } from '../../features/careSourceProcessing/medicationSourceSuggestions';
import AppointmentCaptureScheduleAction from '../../features/appointments/AppointmentCaptureScheduleAction';

const TEXT_PRIMARY = '#1F2937';
const TEXT_SECONDARY = '#4B5563';

const SourceOriginalNotePanel = ({
  originalNote = '',
  sourceAttachments = [],
  sourceNoteId = '',
  sourceNotePath = '',
  captureDetailsKey = '',
  captureDetailsChildId = '',
  variant = 'panel',
  showSourceReviewActions = true,
  showGenericCareDetails = true,
  showOriginalNoteSection = true,
  requireKnowledgeItems = false,
  onHasReviewContent = null,
  onReviewMedication = null,
  appointmentTimeZone = '',
  canManageAppointments = false,
  closedLabelOverride = '',
  openLabelOverride = '',
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [previewAttachment, setPreviewAttachment] = React.useState(null);
  const [reviewSource, setReviewSource] = React.useState(null);
  const [reviewError, setReviewError] = React.useState('');
  const [reviewLoading, setReviewLoading] = React.useState(false);
  const [reviewResolved, setReviewResolved] = React.useState(false);
  const panelId = React.useId();
  const allSourceAttachments = [
    ...(Array.isArray(sourceAttachments) ? sourceAttachments : []),
    ...(Array.isArray(reviewSource?.audioAttachments) ? reviewSource.audioAttachments : []),
    ...(Array.isArray(reviewSource?.sourceMetadata?.audioAttachments) ? reviewSource.sourceMetadata.audioAttachments : []),
  ].filter((attachment, index, attachments) => (
    attachment?.url && attachments.findIndex((candidate) => candidate?.url === attachment.url) === index
  ));
  const isAudioAttachment = (attachment) => String(
    attachment?.type || attachment?.mimeType || attachment?.contentType || ''
  ).toLowerCase().startsWith('audio');
  const audioAttachments = allSourceAttachments.filter(isAudioAttachment);
  const imageAttachments = allSourceAttachments.filter((attachment) => !isAudioAttachment(attachment));
  const hasImageSource = imageAttachments.length > 0;
  const hasAudioSource = audioAttachments.length > 0;
  const cleanSourceNoteId = String(
    sourceNoteId
    || String(sourceNotePath || '').split('/').filter(Boolean).pop()
    || ''
  ).trim();
  const organizedSourceId = cleanSourceNoteId || String(captureDetailsKey || '').trim();
  const reviewableCandidates = React.useMemo(() => (
    showSourceReviewActions && reviewSource ? buildReviewableCandidatesFromCareSource(reviewSource) : []
  ), [reviewSource, showSourceReviewActions]);
  const genericReviewSource = React.useMemo(() => (
    showSourceReviewActions && reviewSource ? buildGenericCareKnowledgeSource(reviewSource) : null
  ), [reviewSource, showSourceReviewActions]);
  const hasGenericSuggestions = React.useMemo(() => (
    showSourceReviewActions && reviewSource ? splitReviewableCareKnowledgeSuggestions(reviewSource).generic.length > 0 : false
  ), [reviewSource, showSourceReviewActions]);

  React.useEffect(() => {
    let active = true;

    if (!cleanSourceNoteId) {
      setReviewSource(null);
      setReviewError('');
      setReviewLoading(false);
      setReviewResolved(true);
      return () => {
        active = false;
      };
    }

    setReviewResolved(false);
    setReviewLoading(true);
    fetchCareSourceById(cleanSourceNoteId)
      .then((source) => {
        if (active) {
          setReviewSource(source);
          setReviewError(source ? '' : "We couldn't find the linked review source.");
        }
      })
      .catch((error) => {
        if (active) {
          setReviewError(error?.message || "We couldn't load source review items.");
        }
      })
      .finally(() => {
        if (active) {
          setReviewLoading(false);
          setReviewResolved(true);
        }
      });

    return () => {
      active = false;
    };
  }, [cleanSourceNoteId]);

  const hasReviewItems = reviewableCandidates.length > 0 || hasGenericSuggestions;
  const hasMedicationReview = showSourceReviewActions
    && reviewSource
    && hasMedicationSourceSuggestions(reviewSource)
    && onReviewMedication;

  // Keep a ref to the latest callback so the effect below can call it without
  // listing it as a dep — prevents re-firing whenever the parent re-renders
  // with a new inline arrow (which would otherwise create a feedback loop).
  const onHasReviewContentRef = React.useRef(onHasReviewContent);
  React.useEffect(() => {
    onHasReviewContentRef.current = onHasReviewContent;
  });

  React.useEffect(() => {
    if (!requireKnowledgeItems || reviewLoading || !reviewResolved) return;
    onHasReviewContentRef.current?.(hasReviewItems);
  }, [requireKnowledgeItems, reviewLoading, reviewResolved, hasReviewItems]);

  if (!originalNote && !hasImageSource && !hasAudioSource) {
    return null;
  }

  if (requireKnowledgeItems) {
    if (reviewLoading || (!reviewSource && cleanSourceNoteId)) return null;
    if (!hasReviewItems) return null;
  }
  const header = variant === 'header';
  const inline = (variant === 'inline' && !hasImageSource && !hasAudioSource) || header;
  const closedLabel = closedLabelOverride || (header
    ? 'View exactly what was captured'
    : (hasImageSource || hasAudioSource
    ? 'View original source'
    : 'View original note'));
  const openLabel = openLabelOverride || (header
    ? 'Hide source'
    : (hasImageSource || hasAudioSource
    ? 'Hide original source'
    : 'Hide original note'));

  return (
    <Box sx={{
      px: inline ? 0 : { xs: 1.45, md: 1.65 },
      py: inline ? 0.15 : 0.9,
      borderTop: inline ? 'none' : `1px solid ${alpha(colors.semantic.line, 0.78)}`,
    }}>
      {reviewError ? (
        <Alert severity="warning" sx={{ mb: 0.9, borderRadius: '12px' }}>
          {reviewError}
        </Alert>
      ) : null}
      {reviewLoading ? (
        <Alert severity="info" sx={{ mb: 0.9, borderRadius: '12px' }}>
          Loading review items...
        </Alert>
      ) : null}
      {showSourceReviewActions && reviewSource ? (
        <Box sx={{ mb: 0.9 }}>
          <AppointmentCaptureScheduleAction
            sources={[reviewSource]}
            canManage={canManageAppointments}
            timeZone={appointmentTimeZone}
            onReview={(appointmentDraft) => navigate(
              `/children/${captureDetailsChildId}/appointments`,
              appointmentDraft?.id ? undefined : { state: { appointmentDraft } },
            )}
          />
          {reviewableCandidates.length ? (
            <CareSourceReviewableCandidateActions
              source={reviewSource}
              hasGenericSuggestions={hasGenericSuggestions}
              onError={setReviewError}
              timelinePresentation
            />
          ) : null}
          {hasGenericSuggestions && genericReviewSource ? (
            <Box sx={{ mt: reviewableCandidates.length ? 0.9 : 0 }}>
              <CareKnowledgeSuggestionReview
                source={genericReviewSource}
                onError={setReviewError}
                timelinePresentation
                showTimelineDetails={showGenericCareDetails}
              />
            </Box>
          ) : null}
          {showOriginalNoteSection && hasMedicationReview ? (
            <ButtonBase
              onClick={() => onReviewMedication(reviewSource)}
              sx={{
                mt: 0.4,
                py: 0.35,
                color: colors.brand.deep,
                fontSize: '0.82rem',
                fontWeight: 850,
              }}
            >
              Review medication details →
            </ButtonBase>
          ) : null}
        </Box>
      ) : null}
      {captureDetailsKey && organizedSourceId ? (
        <Box sx={{ mb: 0.75 }}>
          <Typography sx={{ mb: 0.65, fontSize: '0.78rem', color: alpha(TEXT_SECONDARY, 0.84) }}>
            Includes details not shown on Timeline.
          </Typography>
          <ButtonBase
            component={RouterLink}
            to={`/sources/${encodeURIComponent(organizedSourceId)}`}
            state={{
              childId: captureDetailsChildId || null,
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.7,
              minHeight: 38,
              px: 1.15,
              py: 0.65,
              borderRadius: '12px',
              border: `1px solid ${alpha(colors.brand.deep, 0.34)}`,
              color: colors.brand.deep,
              textAlign: 'left',
              '&:hover': { bgcolor: alpha(colors.brand.deep, 0.06) },
            }}
          >
            <DescriptionIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 900, lineHeight: 1.2 }}>
              View organized capture
            </Typography>
            <ArrowForwardIcon sx={{ fontSize: 17 }} />
          </ButtonBase>
        </Box>
      ) : null}
      {showOriginalNoteSection ? (
        <>
          <ButtonBase
            onClick={() => setOpen((current) => !current)}
            aria-expanded={open}
            aria-controls={panelId}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.55,
              borderRadius: 999,
              px: 0.1,
              py: 0.25,
              color: inline ? alpha(TEXT_SECONDARY, 0.78) : TEXT_PRIMARY,
              textAlign: 'left',
            }}
          >
            {!header && (open ? (
              <ExpandMoreIcon sx={{ fontSize: 19, color: TEXT_SECONDARY }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 19, color: TEXT_SECONDARY, transform: 'rotate(-90deg)' }} />
            ))}
            <Typography sx={{
              fontSize: header ? '0.8rem' : (inline ? '0.86rem' : '0.82rem'),
              fontWeight: 900,
              lineHeight: 1.2,
              color: header ? colors.brand.deep : 'inherit',
            }}>
              {open ? openLabel : closedLabel}
            </Typography>
          </ButtonBase>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              id={panelId}
              sx={{
                mt: 0.65,
                p: inline ? 0 : { xs: 1.05, md: 1.2 },
                pl: inline ? 1.15 : undefined,
                borderRadius: inline ? 0 : '16px',
                bgcolor: inline ? 'transparent' : alpha(colors.semantic.sageMist, 0.36),
                border: inline ? 'none' : `1px solid ${alpha(colors.semantic.line, 0.78)}`,
                borderLeft: inline ? `2px solid ${alpha(colors.semantic.line, 0.9)}` : undefined,
              }}
            >
              <Stack spacing={1.05}>
                {hasAudioSource ? (
                  <Box>
                    <Typography sx={{ mb: 0.55, fontSize: '0.68rem', lineHeight: 1.25, fontWeight: 900, letterSpacing: 0.35, color: alpha(TEXT_SECONDARY, 0.88), textTransform: 'uppercase' }}>
                      Original recording
                    </Typography>
                    <Stack spacing={0.8}>
                      {audioAttachments.map((attachment, index) => (
                        <Box key={`${attachment.url}-${index}`}>
                          {attachment.fileName ? <Typography sx={{ mb: 0.4, fontSize: '0.76rem', fontWeight: 700, color: TEXT_SECONDARY }}>{attachment.fileName}</Typography> : null}
                          <Box component="audio" controls src={attachment.url} sx={{ width: '100%', display: 'block' }} />
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                ) : null}
                {hasImageSource ? (
                  <Box>
                    <Typography
                      sx={{
                        mb: 0.55,
                        fontSize: '0.68rem',
                        lineHeight: 1.25,
                        fontWeight: 900,
                        letterSpacing: 0.35,
                        color: alpha(TEXT_SECONDARY, 0.88),
                        textTransform: 'uppercase',
                      }}
                    >
                      Original image
                    </Typography>
                    <Stack direction="row" spacing={0.8} sx={{ overflowX: 'auto', pb: 0.2 }}>
                      {imageAttachments.map((attachment, index) => (
                        <ButtonBase
                          key={`${attachment.url}-${index}`}
                          onClick={() => setPreviewAttachment(attachment)}
                          sx={{
                            width: 96,
                            height: 76,
                            flex: '0 0 auto',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            border: `1px solid ${alpha(colors.semantic.line, 0.9)}`,
                            bgcolor: '#FFFFFF',
                          }}
                        >
                          <Box
                            component="img"
                            src={attachment.url}
                            alt={attachment.fileName || 'Original uploaded source'}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              display: 'block',
                            }}
                          />
                        </ButtonBase>
                      ))}
                    </Stack>
                  </Box>
                ) : null}
                {reviewSource?.staffOriginalVersion || reviewSource?.sourceMetadata?.staffSigningEnabled ? (
                  <StaffOriginalSection source={reviewSource} compact />
                ) : originalNote ? (
                  <Box>
                    {!inline ? (
                      <Typography
                        sx={{
                          mb: 0.45,
                          fontSize: '0.68rem',
                          lineHeight: 1.25,
                          fontWeight: 900,
                          letterSpacing: 0.35,
                          color: alpha(TEXT_SECONDARY, 0.88),
                          textTransform: 'uppercase',
                        }}
                      >
                        {hasImageSource ? 'Text Lifelog found' : 'Saved exactly as entered'}
                      </Typography>
                    ) : null}
                    <Typography
                      sx={{
                        fontSize: inline ? '0.9rem' : '0.82rem',
                        lineHeight: 1.52,
                        color: TEXT_SECONDARY,
                        fontStyle: inline ? 'italic' : 'normal',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        maxHeight: '32vh',
                        overflowY: 'auto',
                      }}
                    >
                      {originalNote}
                    </Typography>
                  </Box>
                ) : null}
              </Stack>
            </Box>
          </Collapse>
        </>
      ) : null}
      <Dialog
        open={Boolean(previewAttachment)}
        onClose={() => setPreviewAttachment(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: '18px', sm: '22px' },
            overflow: 'hidden',
            bgcolor: '#FFFFFF',
          },
        }}
      >
        <Box sx={{ position: 'relative', bgcolor: '#FFFFFF' }}>
          <IconButton
            aria-label="Close image preview"
            onClick={() => setPreviewAttachment(null)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 2,
              bgcolor: alpha('#FFFFFF', 0.86),
              '&:hover': {
                bgcolor: '#FFFFFF',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          {previewAttachment?.url ? (
            <Box
              component="img"
              src={previewAttachment.url}
              alt={previewAttachment.fileName || 'Original uploaded source'}
              sx={{
                width: '100%',
                maxHeight: '82vh',
                objectFit: 'contain',
                display: 'block',
                bgcolor: alpha(colors.semantic.sageMist, 0.28),
              }}
            />
          ) : null}
        </Box>
      </Dialog>
    </Box>
  );
};

export default SourceOriginalNotePanel;
